import update from 'immutability-helper';
import * as React from 'react';
import { useScreenSize } from '../shared/hooks/useScreenSize';
import { Filter } from './filters/Filter';
import { IFilter } from './filters/IFilter';
import { ToggleExpanded } from './filters/ToggleExpanded';

const dispatchTableFilterEvent = (target: string, filters: IFilter[]) => {
  const customEvent = new CustomEvent(`${target}:filter`, {
    detail: {
      filters: [...filters],
    },
  });
  document.dispatchEvent(customEvent);
};

const attemptJsonParse = (string: string) => {
  try {
    const parsed = JSON.parse(string);
    return parsed;
  } catch {
    // was not valid JSON
    return null;
  }
};

const extractValueFromString = (string: string) => {
  // now, this value might seemingly be utter rubbish
  // our goal here seems to be to over as many situations as possible.

  // number?
  let regex = /^(?<number>\d+)$/;
  let result = string.match(regex);
  if (result) {
    return [result.groups.number];
  }

  // single quoted number?
  regex = /^'(?<number>\d+)'$/;
  result = string.match(regex);
  if (result) {
    return [result.groups.number];
  }

  // double quoted number?
  regex = /^"(?<number>\d+)"$/;
  result = string.match(regex);
  if (result) {
    return [result.groups.number];
  }

  // is it a valid JSON value?
  let parsed = attemptJsonParse(string);

  if (!parsed) {
    // what if we gsub all single quotes (gross)
    const modifiedString = string.replace(/'/g, '"');
    parsed = attemptJsonParse(modifiedString);
  }

  if (!parsed) {
    // what if we gsub all single quotes and wrap it in brackets (gross)
    const modifiedString = `[${string.replace(/'/g, '"')}]`;
    parsed = attemptJsonParse(modifiedString);
  }

  if (parsed) {
    // is it an array of something?
    if (Array.isArray(parsed)) {
      return parsed;
    }

    // not an array, is it a string?
    if (typeof parsed === 'string') {
      // is it a string with commas?
      const splitString = parsed.split(',');
      if (splitString.length > 1) {
        // yeah, probably fine
        return splitString;
      }
    }
  }

  return null;
};

interface IFiltersProps {
  isUrlFilterable: boolean;
  filters: IFilter[];
  target?: string;
  handleFilter?(filters: IFilter[]): void;
  visible_columns?: {
    employment_type_column_visible: boolean;
    locations_column_visible: boolean;
    departments_column_visible: boolean;
    divisions_column_visible: boolean;
    requisition_id_column_visible: boolean;
    structure_custom_group_ones_column_visible: boolean;
    workplace_type_column_visible: boolean;
  };
  willUpdateUrl?: boolean;
}

export default function Filters({
  filters,
  handleFilter,
  isUrlFilterable,
  target: targetFromProps,
  visible_columns,
  willUpdateUrl = true,
}: IFiltersProps) {
  const target = React.useMemo(() => targetFromProps, [targetFromProps]);
  const [filtersWithValueState, setFiltersWithValueState] = React.useState(filters);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(true);
  const { isSmallScreenSize } = useScreenSize();

  const extractUrlParams = React.useCallback(() => {
    const queryString = new URLSearchParams(decodeURIComponent(window.location.search));
    const filterValuesFromQueryString = filters.map((filter) => ({
      key: filter.attribute,
      value: queryString.get(filter.attribute),
    }));
    const filterValues: Record<string, string> = Object.assign(
      {},
      ...filterValuesFromQueryString.map(({ key, value }) => ({ [key]: value })),
    );

    setFiltersWithValueState((prev) => {
      // build an update spec to use with immutability helper
      const updateSpec = Object.assign(
        {},
        ...prev.map(({ attribute, items }, index) => {
          const valueFromUrl = filterValues[attribute];
          if (valueFromUrl) {
            // we have a value from the URL
            const values = extractValueFromString(valueFromUrl);
            // we only care about valid values
            const validValues: number[] = values?.filter((value) => {
              const itemsStringValues = items.map((item) => String(item.value));
              if (itemsStringValues.includes(String(value))) return true;
              return false;
            });
            if (validValues) {
              return { [index]: { values: { $set: validValues } } };
            }
          }
          return null;
        }),
      );

      const newFilterValues = update(prev, updateSpec);
      return newFilterValues;
    });
  }, [filters]);

  React.useEffect(() => {
    // will run on first component mount
    if (isUrlFilterable) extractUrlParams();
  }, [isUrlFilterable, extractUrlParams]);

  React.useEffect(() => {
    if (target) {
      const dispatch = () => dispatchTableFilterEvent(target, filtersWithValueState);
      dispatch();

      document.addEventListener(`${target}:ready`, dispatch);
      return () => document.removeEventListener(`${target}:ready`, dispatch);
    }
    return () => {};
  }, [target, filtersWithValueState]);

  const updateSelectedFilters = React.useCallback((attribute: string, values: number[]) => {
    setFiltersWithValueState((prev) => {
      const index = prev.findIndex((filter) => filter.attribute === attribute);
      const updateSpec = {
        [index]: { values: { $set: values } },
      };
      const updatedValues = update(prev, updateSpec);
      return updatedValues;
    });
  }, []);

  React.useEffect(() => {
    if (handleFilter) handleFilter(filtersWithValueState);
  }, [handleFilter, filtersWithValueState]);

  const visibleFilters =
    visible_columns == null
      ? filtersWithValueState
      : filtersWithValueState.filter((iteratingFilter: IFilter) => {
          switch (iteratingFilter.name) {
            case 'location':
              return visible_columns.locations_column_visible;
            case 'department':
              return visible_columns.departments_column_visible;
            case 'division':
              return visible_columns.divisions_column_visible;
            case 'employment_type':
              return visible_columns.employment_type_column_visible;
            case 'requisition_id':
              return visible_columns.requisition_id_column_visible;
            case 'structure_custom_group_one':
              return visible_columns.structure_custom_group_ones_column_visible;
            case 'workplace_type_column_visible':
              return visible_columns.workplace_type_column_visible;
            default:
              return true;
          }
        });

  return (
    <>
      {isSmallScreenSize && <ToggleExpanded isExpanded={isExpanded} setIsExpanded={setIsExpanded} />}
      <div className="frow frow--gutters frow--gutters-md-2x frow--justify-center">
        {(!isSmallScreenSize || isExpanded) && (
          <>
            {visibleFilters.map((filter: IFilter, index: number) => (
              <div key={filter.attribute} className={`col-tn-1-${visibleFilters.length}`}>
                <Filter filter={filter} willUpdateUrl={willUpdateUrl} updateSelectedFilters={updateSelectedFilters} />
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}
